'use client';

/**
 * ErrorSummary — the top-of-form box after a failed submit (Complete_app
 * §16.4 "accessible form validation", §17.1 "server-error recovery").
 *
 *     <ErrorSummary message={formMessage} errors={fieldErrors} labels={{ title: 'Title' }} />
 *
 * - `role="alert"`, and it takes focus when it appears (or when the errors
 *   change), so a keyboard or screen-reader user lands on it.
 * - Each field error is a link to `#field-<key>` (the id `FormField name=`
 *   gives) that focuses the input.
 * - Renders nothing when there is nothing to say.
 */
import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { cn } from './cn';
import { fieldDomId } from './FormField';

export interface ErrorSummaryProps {
  /** Form-level sentence (from `formErrorsFromApi`). */
  message?: string | null;
  /** Field key → sentence. */
  errors?: Record<string, string | null | undefined>;
  /** Field key → its label, for the link text. Unlabelled keys show the message alone. */
  labels?: Record<string, string>;
  title?: string;
  /** Default true: move focus here when it appears. */
  autoFocus?: boolean;
  /** Change it (e.g. the submit count) to move focus here again while visible. */
  focusKey?: string | number;
  className?: string;
}

export function ErrorSummary({
  message,
  errors = {},
  labels = {},
  title,
  autoFocus = true,
  focusKey,
  className,
}: ErrorSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  const entries = Object.entries(errors).filter((e): e is [string, string] => Boolean(e[1]));
  const visible = Boolean(message) || entries.length > 0;

  // Focus when the summary appears, or when `focusKey` changes (pass the
  // submit count) — not on every edit, which would pull focus away from a
  // field validated on blur.
  useEffect(() => {
    if (visible && autoFocus) ref.current?.focus();
  }, [visible, autoFocus, focusKey]);

  if (!visible) return null;

  const heading =
    title ?? (entries.length > 0 ? 'Please fix the highlighted fields' : 'That did not go through');

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-state-danger-border bg-state-danger-bg p-4 text-sm text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-gold',
        className,
      )}
      role="alert"
      tabIndex={-1}
    >
      <p className="flex items-center gap-2 font-medium text-state-danger">
        <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
        {heading}
      </p>
      {message ? <p className="mt-1 text-fg-secondary">{message}</p> : null}
      {entries.length > 0 ? (
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {entries.map(([key, text]) => {
            const target = fieldDomId(key);
            return (
              <li key={key}>
                <a
                  className="text-state-danger underline underline-offset-2 hover:no-underline"
                  href={`#${target}`}
                  onClick={(e) => {
                    const el = document.getElementById(target);
                    if (el) {
                      e.preventDefault();
                      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      (el as HTMLElement).focus({ preventScroll: true });
                    }
                  }}
                >
                  {labels[key] ? `${labels[key]}: ${text}` : text}
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
